import { useEffect, useRef, useState } from 'react';
import { FaGlobe, FaSpinner } from 'react-icons/fa';
import axios from 'axios';
import { aiApi } from '../../api/aiApi';
import toast from 'react-hot-toast';
import Button from '../common/Button';

interface AIExplanationProps {
  code: string;
  description: string;
  codeType: string;
}

const AIExplanation = ({ code, description, codeType }: AIExplanationProps) => {
  const [language, setLanguage] = useState('English');
  const [explanation, setExplanation] = useState('');
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const languages = ['English', 'Urdu', 'Spanish'];

  useEffect(() => () => abortRef.current?.abort(), []);

  const generateExplanation = async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const response = await aiApi.explainCode({ code, description, language, codeType }, controller.signal);
      if (abortRef.current === controller) setExplanation(response.explanation);
    } catch (error) {
      if (axios.isCancel(error)) return;
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (status === 402) toast.error('Your subscription does not cover this feature');
      else if (status === 403) toast.error('You are not authorized to use this feature');
      else if (status === 500) toast.error('The AI service is currently unavailable, please try again later');
      else toast.error('Failed to generate explanation');
      console.error(error);
    } finally {
      if (abortRef.current === controller) setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold flex items-center space-x-2">
          <span>🤖</span>
          <span>AI Explanation</span>
        </h2>
        <div className="flex items-center space-x-2">
          {/* @ts-ignore */}
          <FaGlobe className="text-gray-500" />
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {languages.map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>
        </div>
      </div>

      <p className="text-xs text-gray-500 mb-4">AI-generated explanation — for informational purposes only</p>

      {!explanation && !loading && (
        <div className="text-center py-8">
          <p className="text-gray-600 mb-4">
            Get a simple, easy-to-understand explanation of this code
          </p>
          <Button onClick={generateExplanation}>
            Generate Explanation
          </Button>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-8">
          {/* @ts-ignore */}
          <FaSpinner className="animate-spin text-primary-600 text-3xl" />
        </div>
      )}

      {explanation && !loading && (
        <div>
          <div className="bg-primary-50 p-4 rounded-lg mb-4">
            <p className="text-gray-800 leading-relaxed">{explanation}</p>
          </div>
          <Button onClick={generateExplanation} variant="secondary">
            Regenerate
          </Button>
        </div>
      )}
    </div>
  );
};

export default AIExplanation;